import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import GoogleAuthButton from "Components/GoogleAuthButton";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RouteUrls } from "../../../Constants/RouteUrls";
import { registrationFormData } from "./Data";
import { FormDataType } from "DataTypes/Form";
import { commonFormSwitch } from "Utils/SwitchCases";
import { currentYear, HandleFormChange, ValidateFormData } from "Utils/Common";
// import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ControlledForm from "Components/ControlledForm";
import authApi from "Service/Auth.api";
import ImgPath from "Constants/Imgs";
import { OrgDropArr } from "Constants/Arrays";
import OtpInput from "Components/OtpInput";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../../Redux/Screen/Action";

const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const NriRegister = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlPath = window.location.pathname;
  const isCorporate = urlPath.includes(RouteUrls.corporate);
  const [formData, setFormData] = useState<FormDataType[]>([]);
  const [emailData, setEmailData] = useState<{
    fieldVal: string;
    otpSend: boolean;
    otpVal: string;
    otpVerified: boolean;
  }>({
    fieldVal: "",
    otpSend: false,
    otpVal: "",
    otpVerified: false,
  });
  const [contactData, setContactData] = useState<{
    fieldVal: string;
    otpSend: boolean;
    otpVal: string;
    otpVerified: boolean;
  }>({
    fieldVal: "",
    otpSend: false,
    otpVal: "",
    otpVerified: false,
  });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [otpLoadingType, setOtpLoadingType] = useState<"email" | "contact" | null>(null);

  // Timer
  const [emailTimer, setEmailTimer] = useState(0);
  const emailTimerInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const newArr = JSON.parse(JSON.stringify(registrationFormData()));
    setFormData(newArr);
  }, []);

  useEffect(() => {
    return () => {
      if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
    }
  }, []);

  const startEmailTimer = () => {
    setEmailTimer(30);
    if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
    emailTimerInterval.current = setInterval(() => {
      setEmailTimer((prev) => {
        if (prev <= 1) {
          if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const handleSendOtp: (otpType: "email" | "contact") => void = async (
    otpType
  ) => {

    if (otpType === "email") {
      if (!validateEmail(emailData.fieldVal)) return; // Extra safety

      if (!emailData.fieldVal || emailData.fieldVal.trim() === "") {
        dispatch(setAlertData({ alertMessage: "Please enter a valid email address first.", alertVariant: "error", openAlert: true }));
        return;
      }
      setOtpLoadingType("email"); // Set loading for email only
      dispatch(startLoading({ loading: true, loadtext: "Sending OTP..." }));
      try {
        const res = await authApi.emailSendOtp({
          email: emailData.fieldVal,
          requestType: "register",
        });
        if (res?.success) {
          setEmailData({
            ...emailData, // Keep existing field value
            otpSend: true,
            otpVal: "",
            otpVerified: false,
          });
          startEmailTimer();
        }
      }
      catch (error) {
        console.error("Email OTP Error:", error);
      } finally {
        setOtpLoadingType(null); // Stop Loader
        dispatch(stopLoading());
      }
    }
  };
  const handleVerifyOtp: (otpType: "email" | "contact") => void = async (
    otpType
  ) => {
    if (otpType === "email") {
      if (emailData.otpVal.length !== 4) {
        dispatch(setAlertData({ alertMessage: "Please enter valid 4-digit OTP", alertVariant: "error", openAlert: true }));
        return;
      }
      dispatch(startLoading({ loading: true, loadtext: "Verifying OTP..." }));
      try {
        const res = await authApi.verifyOtp({
          mobileNumber: null,
          email: emailData.fieldVal,
          otp: emailData.otpVal,
        });
        if (res?.success) {
          setEmailData({
            fieldVal: emailData.fieldVal,
            otpSend: true,
            otpVal: emailData.otpVal,
            otpVerified: true,
          });
          if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
          setEmailTimer(0);
        }
      } catch (error) {
        console.error("Verify OTP Error:", error);
      } finally {
        dispatch(stopLoading());
      }
    }
  };

  const handleRegister = async () => {
    if (!emailData.otpVerified) {
      dispatch(setAlertData({ alertMessage: "Please verify your email first.", alertVariant: "error", openAlert: true }));
      return;
    }

    // Validate name/password BEFORE anything else — this used to have zero field validation at
    // all, so a blank name/password would reach the API with the loading spinner already showing.
    const { formData: validated, errorCheck } = ValidateFormData(formData);
    setFormData(validated);
    if (errorCheck) {
      dispatch(setAlertData({ alertMessage: "Please fix the highlighted fields.", alertVariant: "error", openAlert: true }));
      return;
    }

    // Define a default Date of Birth (e.g., 18 years ago)
    const defaultDOB = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString();

    const payload = {
      firstName: formData[0]?.value ?? "",
      lastName: formData[1]?.value ?? "",
      password: formData[2]?.value ?? "", // Check index, usually password follows profile info
      address: formData[7]?.value ?? "Not Provided",
      city: formData[8]?.value ?? "",
      state: formData[4]?.value ?? "",
      postalCode: formData[5]?.value ?? "",
      dateOfBirth: formData[6]?.value ?? defaultDOB,
      description: formData[7]?.value ?? "",
      email: emailData?.fieldVal ?? "",
      ContactNumber: "0000000000", // Default placeholder

      // userType: "User",
    };

    dispatch(startLoading({ loading: true, loadtext: "Registering..." }));
    try {
      const res = await authApi.registerNriUser(payload);
      if (res?.success) {
        navigate(`/${RouteUrls.login}`);
      }
    } catch (error) {
      console.error("Registration Error:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const handlePasswordToggle = (fieldId: string) => {
    setFormData((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, showPassword: !field.showPassword }
          : field
      )
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        overflowY: "auto"
      }}
    >
      {/* --- MINIMALIST HEADER --- */}
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src={"https://nolegalpaisa.com/assets/images/new_logo.webp"}
          alt="Logo"
          sx={{ height: "60px", cursor: "pointer" }}
          onClick={() => (window.location.href = "/home.html")}
        />
      </Box>

      <Container maxWidth="md" sx={{ pb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 6,
            boxShadow: "0px 20px 50px rgba(0,0,0,0.08)",
            bgcolor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)"
          }}
        >
          {/* --- FORM TITLE --- */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Avatar
              sx={{
                m: "0 auto 16px",
                bgcolor: "primary.main",
                width: 60,
                height: 60,
                boxShadow: "0 8px 20px rgba(25, 118, 210, 0.2)"
              }}
            >
            </Avatar>
            <Typography variant="h4" fontWeight={800} sx={{ color: "#1a202c", mb: 1 }}>
              NRI Registration
            </Typography>
            <Typography variant="body1" color="text.primary">
              Global legal assistance for Non-Resident Indians
            </Typography>
          </Box>

          <GoogleAuthButton userType="NRI" />

          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="textSecondary" sx={{ px: 2, fontWeight: 600 }}>
              OR FILL REGISTRATION FORM
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          <Box component="form" noValidate>
            <Grid container spacing={2.5}>

              {/* 1. DYNAMIC FIELDS */}
              {formData.length > 0 &&
                formData.map((el: FormDataType) => (
                  <Grid
                    key={el.id}
                    size={{ xs: 12, sm: el.boxSize === 3 ? 6 : 12 }}
                  >
                    {commonFormSwitch({
                      formObj: {
                        ...el,
                        onPasswordToggle: el.hasPasswordToggle
                          ? () => handlePasswordToggle(el.id)
                          : undefined,
                      },
                      onChange: (data) =>
                        setFormData(HandleFormChange(data, formData)),
                    })}
                  </Grid>
                ))}

              {/* 2. VERIFICATION SECTION */}
              {/* <Grid size={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 1 }}>
                  Identity & Contact Verification
                </Typography>
              </Divider>
            </Grid> */}

              {/* --- EMAIL SECTION --- */}
              <Grid size={{ xs: 12 }}>
                <ControlledForm.Input
                  id="email"
                  label="Email Address"
                  error={emailData.fieldVal !== "" && !validateEmail(emailData.fieldVal)}
                  infoText={emailData.fieldVal !== "" && !validateEmail(emailData.fieldVal) ? "Invalid email format" : ""}
                  value={emailData.fieldVal}
                  {...(emailData.otpVerified && { focused: true, borderColor: "success", readOnly: true })}
                  onChange={(value) => setEmailData({ ...emailData, fieldVal: value as string })}
                />
              </Grid>

              {emailData.otpSend && !emailData.otpVerified && (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <OtpInput
                      length={4}
                      value={emailData.otpVal}
                      onChange={(val) => setEmailData({ ...emailData, otpVal: val })}
                      onComplete={() => { }}
                    />
                    <Button
                      disabled={emailTimer > 0}
                      onClick={() => handleSendOtp("email")}
                      sx={{ mt: 1, textTransform: 'none' }}
                      size="small"
                    >
                      {emailTimer > 0 ? `Resend Email OTP in ${emailTimer}s` : "Resend Email OTP"}
                    </Button>
                  </Box>
                </Grid>
              )}

              <Grid sx={{ xs: 12 }}>
                {!emailData.otpVerified ? (
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ height: '56px', borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
                    onClick={() => emailData.otpSend ? handleVerifyOtp("email") : handleSendOtp("email")}
                    disabled={otpLoadingType === "email" || !emailData.fieldVal?.trim()}
                  >
                    {otpLoadingType === "email" ? "..." : emailData.otpSend ? "Verify Email OTP" : "Send Email OTP"}
                  </Button>
                ) : (
                  <Button fullWidth variant="outlined" color="success" disabled sx={{ height: '56px', borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
                    Email Verified
                  </Button>
                )}
              </Grid>

            </Grid>

            {/* --- FINAL ACTION BUTTON --- */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{
                mt: 6,
                py: 2,
                borderRadius: 3,
                fontSize: "1.1rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 10px 25px rgba(25, 118, 210, 0.25)",
                transition: "0.3s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 15px 30px rgba(25, 118, 210, 0.3)" }
              }}
              onClick={handleRegister}
              disabled={!emailData.otpVerified}
            >
              Create NRI Account
            </Button>

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.primary">
                Already have an account?{" "}
                <Typography
                  component="span"
                  variant="body2"
                  onClick={() => navigate(`/${RouteUrls.login}`)}
                  sx={{ color: "primary.main", fontWeight: 700, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Sign In
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 4, color: "text.disabled" }}>
          Secure Registration for Global Indians. © {currentYear} NoLegalPaisa
        </Typography>
      </Container>
    </Box>
  );

};

export default NriRegister;
